import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['stepLink', 'panel', 'stepLine', 'prevButton', 'nextButton', 'submitButton']
  static values = { active: String, prevLabel: String, nextLabel: String }

  connect() {
    this.sync()
  }

  goto(event) {
    const target = event.currentTarget.dataset.step
    if (!target) return
    const order = this.stepLinkTargets.map((el) => el.dataset.step)
    if (order.indexOf(target) > order.indexOf(this.activeValue) && !this.reportActivePanelValidity()) return
    if (target !== this.activeValue) {
      this.activeValue = target
      this.scrollToTop()
    }
  }

  activeValueChanged() {
    this.sync()
  }

  next() {
    if (!this.reportActivePanelValidity()) return
    const order = this.stepLinkTargets.map((el) => el.dataset.step)
    const idx = order.indexOf(this.activeValue)
    if (idx >= 0 && idx < order.length - 1) this.activeValue = order[idx + 1]
    this.scrollToTop()
  }

  // Native constraint validation (min, step…) on the visible fields of the
  // current step — the wizard is a single <form>, so the browser alone would
  // only validate on the final submit.
  reportActivePanelValidity() {
    const panel = this.panelTargets.find((p) => p.dataset.step === this.activeValue)
    if (!panel) return true
    const fields = [...panel.querySelectorAll('input, select, textarea')]
    const valid = fields.every((field) => field.offsetParent === null || field.reportValidity())
    this.stepLinkTargets.find((el) => el.dataset.step === this.activeValue)?.classList.toggle('has-errors', !valid)
    return valid
  }

  prev() {
    const order = this.stepLinkTargets.map((el) => el.dataset.step)
    const idx = order.indexOf(this.activeValue)
    if (idx > 0) this.activeValue = order[idx - 1]
    this.scrollToTop()
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  format(template, current, total) {
    if (!template) return ''
    return template.replace('%{current}', current).replace('%{total}', total)
  }

  sync() {
    const order = this.stepLinkTargets.map((el) => el.dataset.step)
    const activeIndex = order.indexOf(this.activeValue)
    const total = order.length

    this.stepLinkTargets.forEach((link, i) => {
      link.classList.toggle('is-active', i === activeIndex)
      link.classList.toggle('is-completed', i < activeIndex)
    })
    this.panelTargets.forEach((panel) => {
      const isActive = panel.dataset.step === this.activeValue
      panel.classList.toggle('is-active', isActive)
      panel.setAttribute('aria-hidden', String(!isActive))
    })
    if (this.hasStepLineTarget) {
      this.stepLineTargets.forEach((line) => {
        const afterIndex = order.indexOf(line.dataset.stepAfter)
        line.classList.toggle('is-active', afterIndex < activeIndex)
      })
    }

    const isLast = activeIndex === total - 1
    const isFirst = activeIndex === 0

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.hidden = isFirst
      if (!isFirst) {
        this.prevButtonTarget.textContent = this.format(this.prevLabelValue, activeIndex, total)
      }
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.hidden = isLast
      if (!isLast) {
        this.nextButtonTarget.textContent = this.format(this.nextLabelValue, activeIndex + 2, total)
      }
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.hidden = !isLast
    }
  }
}
